import BN from "bignumber.js";
import { hexlify } from "@/common/lib/utils";
import ApiRequest from "@/common/lib/apiRequest";
import ERC20ABI from "@/common/config/abi/ERC20.json";
import migrationModule from "./migration.module";

const mutationTypes = {
  FETCH_AVAILABLE_BALANCE: "kyber/FETCH_AVAILABLE_BALANCE",
  SET_AVAILABLE_BALANCE: "kyber/SET_AVAILABLE_BALANCE",
  SET_STAKED_BALANCE: "kyber/SET_STAKED_BALANCE",
  GET_ALLOWANCE: "kyber/GET_ALLOWANCE",
  SET_ALLOWANCE: "kyber/SET_ALLOWANCE",
  FETCH_CURRENT_EPOCH_NUMBER: "kyber/FETCH_CURRENT_EPOCH_NUMBER",
  SET_CURRENT_EPOCH_NUMBER: "kyber/SET_CURRENT_EPOCH_NUMBER",
  FETCH_FIRST_EPOCH_TS: "kyber/FETCH_FIRST_EPOCH_TS",
  SET_FIRST_EPOCH_TS: "kyber/SET_FIRST_EPOCH_TS",
  SET_TX_ACTION: "kyber/SET_TX_ACTION",
  SET_LATEST_BRR_RATIO: "kyber/SET_LATEST_BRR_RATIO",
  SET_ESTIMATED_APR: "kyber/SET_ESTIMATED_APR",
  SET_STAKED_RATIO: "kyber/SET_STAKED_RATIO",
  SET_TOTAL_BURNT_KNC: "kyber/SET_TOTAL_BURNT_KNC",
  SET_KNC_PRICES: "kyber/SET_KNC_PRICES",
  RESET_STATE: "kyber/RESET_STATE",
};

const state = {
  availableBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  allowance: {
    fetching: false,
    result: null,
    error: null,
  },
  currentEpochNumber: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedBalance: {
    fetching: false,
    result: null,
    error: null,
  },
  firstEpochTs: {
    fetching: false,
    result: null,
    error: null,
  },
  currentTxAction: "",
  estimatedAPR: {
    fetching: false,
    result: null,
    error: null,
  },
  latestBrrRatio: {
    fetching: false,
    result: null,
    error: null,
  },
  stakedRatio: {
    fetching: false,
    result: null,
    error: null,
  },
  totalBurntKnc: {
    fetching: false,
    result: null,
    error: null,
  },
  kncPrices: {
    fetching: false,
    result: null,
    error: null,
  },
};

const actions = {
  async fetchCurrentEpochNumber({ commit }) {
    commit(mutationTypes.FETCH_CURRENT_EPOCH_NUMBER);
    try {
      const result = await ApiRequest.get("/kyberdao/network/epoch/current");
      commit(mutationTypes.SET_CURRENT_EPOCH_NUMBER, { result });
    } catch (error) {
      commit(mutationTypes.SET_CURRENT_EPOCH_NUMBER, { error });
    }
  },
  async fetchStakedBalance({ commit, rootGetters }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.SET_STAKED_BALANCE, { fetching: true, result: null });
    try {
      const result = await ApiRequest.get(
        "/kyberdao/staker/staked_balance/" + walletSelected.address
      );
      commit(mutationTypes.SET_STAKED_BALANCE, { result });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_BALANCE, { error });
    }
  },
  async fetchFirstEpochTs({ commit }) {
    commit(mutationTypes.FETCH_FIRST_EPOCH_TS);
    try {
      const [firstEpochTs, epochPeriod] = await Promise.all([
        ApiRequest.get("/kyberdao/network/epoch/first_timestamp"),
        ApiRequest.get("/kyberdao/network/epoch/time_period"),
      ]);
      const result = {
        ts: firstEpochTs.value,
        period: epochPeriod.value,
      };
      commit(mutationTypes.SET_FIRST_EPOCH_TS, { result });
    } catch (error) {
      commit(mutationTypes.SET_FIRST_EPOCH_TS, { error });
    }
  },
  async fetchAvailableBalance({ commit, rootGetters }) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    commit(mutationTypes.FETCH_AVAILABLE_BALANCE);
    try {
      const result = await ApiRequest.get(
        "kyberdao/staker/available_balance/" + walletSelected.address
      );
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { result });
    } catch (error) {
      commit(mutationTypes.SET_AVAILABLE_BALANCE, { error });
    }
  },
  async getAllowance({ commit, rootGetters }, address) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    address = address || walletSelected?.address;
    if (!address) return;
    commit(mutationTypes.GET_ALLOWANCE);
    try {
      const result = await ApiRequest.get(
        "kyberdao/staker/allowance/" + address
      );
      if (address.toLowerCase() === walletSelected.address?.toLowerCase()) {
        commit(mutationTypes.SET_ALLOWANCE, { result });
      }
      return result;
    } catch (error) {
      commit(mutationTypes.SET_ALLOWANCE, { error });
    }
  },
  async getApprovalRawTx({ rootGetters }, address) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    address = address || walletSelected.address;
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/kyberdao/staking/digest/approve/${address}`),
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getStakeRawTx({ rootGetters }, payload) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/kyberdao/staking/digest/deposit/${address}`, {
        params: {
          amount: payload.amount,
        },
      }),
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getUnstakeRawTx({ rootGetters }, payload) {
    const { address } = rootGetters["userWallets/walletSelected"];
    const [result, { value: nonce }] = await Promise.all([
      ApiRequest.get(`/kyberdao/staking/digest/withdraw/${address}`, {
        params: {
          amount: payload.amount,
        },
      }),
      ApiRequest.get(`/kyberdao/network/nonce/${address}`),
    ]);
    const tx = {
      from: address,
      to: result.contractAddress,
      gas: result.gasLimit,
      value: "0x0",
      nonce: hexlify(nonce),
      data: result.digest,
    };
    return tx;
  },
  async getSendRawTx({ rootGetters, dispatch }, payload) {
    const maxPriorityFeePerGas = rootGetters["appConfig/maxPriorityFeePerGas"];
    const web3 = rootGetters["appConfig/web3"]();
    const walletSelected = rootGetters["userWallets/walletSelected"];
    const getTokenByName = rootGetters["appConfig/getTokenByName"];
    const knc = getTokenByName("knc");
    const contract = new web3.eth.Contract(ERC20ABI, knc.contractAddress, {
      from: walletSelected.address,
    });
    const transferMethod = contract.methods.transfer(
      payload.toAddress,
      hexlify(payload.amount)
    );
    const [{ value: nonce }, gasLimit, { fast }] = await Promise.all([
      ApiRequest.get("/kyberdao/network/nonce/" + walletSelected.address),
      transferMethod.estimateGas(),
      dispatch("appConfig/fetchEthGasPrice", null, { root: true }),
    ]);
    const tx = {
      from: walletSelected.address,
      to: knc.contractAddress,
      maxFeePerGas: hexlify(fast * 1e9),
      maxPriorityFeePerGas: hexlify(maxPriorityFeePerGas),
      gas: hexlify(gasLimit),
      value: "0x0",
      nonce: hexlify(nonce),
      data: transferMethod.encodeABI(),
    };
    return tx;
  },
  async broadcastTx({ rootGetters, dispatch }, payload) {
    const walletSelected = rootGetters["userWallets/walletSelected"];
    let url;
    let body = {
      type: payload.type,
      metadata: payload.metadata,
    };
    if (payload.signedTx) {
      body.signed_tx = payload.signedTx;
      url = `kyberdao/transaction/broadcast/${walletSelected.address}`;
    } else {
      body.tx_hash = payload.txHash;
      url = `kyberdao/transaction/create/${walletSelected.address}`;
    }
    return ApiRequest.post(url, body);
  },
  async fetchLatestBrrRatio({ commit }) {
    try {
      const data = await ApiRequest.get("/kyberdao/network/brr");
      const result = {
        burnRatio: data.burnRatio * 100,
        rewardRatio: data.rewardRatio * 100,
        rebateRatio: data.rebateRatio * 100,
        totalStaked: data.totalStaked,
        totalVoted: data.currentEpochVoted,
        networkFeesCollected: data.networkFeesCollected,
        networkFee: data.networkFee,
        votingRewards: data.votingRewards,
      };
      const totalBurntKnc = {
        value: data.totalBurnedFee,
        current: new BN(data.currentEpochBurn)
          .multipliedBy(data.ethPrice)
          .dividedBy(data.kncPrice)
          .valueOf(),
      };
      commit(mutationTypes.SET_LATEST_BRR_RATIO, { result });
      commit(mutationTypes.SET_TOTAL_BURNT_KNC, { result: totalBurntKnc });
    } catch (error) {
      commit(mutationTypes.SET_LATEST_BRR_RATIO, { error });
    }
  },
  async fetchStakedRatio({ commit, getters }) {
    try {
      const [
        { value: valueCurrent, votedRatio: votedRatioCurrent },
        { value: valuePrevious, votedRatio: votedRatioPrevious },
      ] = await Promise.all([
        ApiRequest.get("/kyberdao/network/staked_ratio/latest"),
        ApiRequest.get(
          "/kyberdao/network/staked_ratio/" + (getters.currentEpochNumber - 1)
        ),
      ]);
      const result = {
        valueCurrent: valueCurrent * 100,
        votedRatioCurrent: votedRatioCurrent * 100,
        valuePrevious: valuePrevious * 100,
        votedRatioPrevious: votedRatioPrevious * 100,
      };
      commit(mutationTypes.SET_STAKED_RATIO, { result });
    } catch (error) {
      commit(mutationTypes.SET_STAKED_RATIO, { error });
    }
  },
  async fetchEstimatedApr({ commit }) {
    try {
      const { value } = await ApiRequest.get(
        "/kyberdao/network/staking_apr/latest"
      );
      commit(mutationTypes.SET_ESTIMATED_APR, {
        result: { value: (value || 0) * 100 },
      });
    } catch (error) {
      commit(mutationTypes.SET_ESTIMATED_APR, { error });
    }
  },
  async fetchAssetPriceChart({ commit }, { startTime, type = "h" } = {}) {
    commit(mutationTypes.SET_KNC_PRICES, { fetching: true });
    try {
      startTime = startTime || Date.now() - 86400000;
      startTime = Math.floor(startTime / 1000);
      const data = await ApiRequest.get(
        "/kyberdao/network/knc_prices/" + startTime,
        { params: { type } }
      );
      const result = getKncPriceData(data, type);
      commit(mutationTypes.SET_KNC_PRICES, { result, fetching: false });
      return result;
    } catch (error) {
      commit(mutationTypes.SET_KNC_PRICES, { error, fetching: false });
    }
  },
  async fetchRequiredInfo({ commit, dispatch }) {
    commit(mutationTypes.SET_ALLOWANCE, { result: null });
    return Promise.all([
      dispatch("getAllowance"),
      dispatch("fetchAvailableBalance"),
      dispatch("fetchStakedBalance"),
    ]);
  },
  async fetchNetworkInfo({ dispatch }) {
    return Promise.all([
      dispatch("fetchFirstEpochTs"),
      dispatch("fetchCurrentEpochNumber"),
      dispatch("fetchAssetPriceChart"),
    ]);
  },
  async fetchAdditionalInfo({ dispatch }) {
    return Promise.all([
      dispatch("fetchEstimatedApr"),
      dispatch("fetchLatestBrrRatio"),
      dispatch("fetchStakedRatio"),
    ]);
  },
  setTxAction({ commit }, action) {
    commit(mutationTypes.SET_TX_ACTION, action);
  },
  resetState({ commit }) {
    commit(mutationTypes.RESET_STATE);
  },
};

const mutations = {
  [mutationTypes.FETCH_AVAILABLE_BALANCE](state) {
    state.availableBalance.fetching = true;
    state.availableBalance.error = null;
  },
  [mutationTypes.SET_AVAILABLE_BALANCE](state, data) {
    state.availableBalance = {
      ...state.availableBalance,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.FETCH_CURRENT_EPOCH_NUMBER](state) {
    state.currentEpochNumber.fetching = true;
    state.currentEpochNumber.error = null;
  },
  [mutationTypes.SET_CURRENT_EPOCH_NUMBER](state, data) {
    state.currentEpochNumber = {
      ...state.currentEpochNumber,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.SET_STAKED_BALANCE](state, data) {
    state.stakedBalance = data;
  },
  [mutationTypes.FETCH_FIRST_EPOCH_TS](state) {
    state.firstEpochTs.fetching = true;
    state.firstEpochTs.error = null;
  },
  [mutationTypes.SET_FIRST_EPOCH_TS](state, data) {
    state.firstEpochTs = {
      ...state.firstEpochTs,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.GET_ALLOWANCE](state) {
    state.allowance.fetching = true;
    state.allowance.error = null;
  },
  [mutationTypes.SET_ALLOWANCE](state, data) {
    state.allowance = {
      ...state.allowance,
      fetching: false,
      ...data,
    };
  },
  [mutationTypes.SET_TX_ACTION](state, action) {
    state.currentTxAction = action;
  },
  [mutationTypes.SET_LATEST_BRR_RATIO](state, data) {
    state.latestBrrRatio = data;
  },
  [mutationTypes.SET_STAKED_RATIO](state, data) {
    state.stakedRatio = data;
  },
  [mutationTypes.SET_TOTAL_BURNT_KNC](state, data) {
    state.totalBurntKnc = data;
  },
  [mutationTypes.SET_ESTIMATED_APR](state, data) {
    state.estimatedAPR = data;
  },
  [mutationTypes.SET_KNC_PRICES](state, data) {
    state.kncPrices = data;
  },
  [mutationTypes.RESET_STATE](state) {
    state.availableBalance = {
      fetching: false,
      result: null,
      error: null,
    };
    state.stakedBalance = {
      fetching: false,
      result: null,
      error: null,
    };
    state.currentTxAction = "";
  },
};

const getKncPriceData = (data, type) => {
  data = data.sort((a, b) => a.timestamp - b.timestamp);
  const result = [];
  for (let index = data.length - 1; index > -1; index -= 1) {
    const elem = data[index];
    result.unshift([elem.timestamp, Number(elem.usd)]);
  }
  return result;
};

const getters = {
  currentTxAction: state => state.currentTxAction,
  availableBalance: state => state.availableBalance,
  stakedBalance: state => state.stakedBalance,
  needUnlockAssets: state => {
    return state.allowance.result?.value <= 0 ?? false;
  },
  currentEpochNumber: state =>
    state.currentEpochNumber.result?.value || state.currentEpochNumber.result,
  firstEpochTs: state => state.firstEpochTs.result,
  estimatedAPR: state => state.estimatedAPR,
  latestBrrRatio: state => state.latestBrrRatio,
  stakedRatio: state => state.stakedRatio,
  totalBurntKnc: state => state.totalBurntKnc,
  epochStartingTime: (state, getters) => {
    const currentEpochNumber = getters.currentEpochNumber;
    const { ts, period } = getters.firstEpochTs || {};
    const current = ts * 1000 + period * 1000 * (currentEpochNumber - 1);
    const next = current + Number(period * 1000);
    return {
      current,
      next,
    };
  },
  kncPrices: state => state.kncPrices,
};

export default {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
  modules: {
    migration: migrationModule,
  },
};
